import { useEffect, useState } from "react";

/**
 * Hook to detect if user prefers reduced motion
 * Respects system accessibility settings
 *
 * Usage:
 * ```tsx
 * const prefersReducedMotion = useReducedMotion();
 *
 * return (
 *   <motion.div
 *     animate={prefersReducedMotion ? {} : { scale: 1.1 }}
 *   >
 *     Content
 *   </motion.div>
 * );
 * ```
 */
export const useReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    // SSR-safe initial value
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    // Legacy browsers
    else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return prefersReducedMotion;
};

/**
 * Get animation props that respect reduced motion preference
 *
 * Usage:
 * ```tsx
 * const prefersReducedMotion = useReducedMotion();
 * const animationProps = getReducedMotionProps(prefersReducedMotion, {
 *   initial: { opacity: 0, y: 20 },
 *   animate: { opacity: 1, y: 0 },
 * });
 * ```
 */
export function getReducedMotionProps<T extends Record<string, unknown>>(
  prefersReducedMotion: boolean,
  animationProps: T
): T | Record<string, never> {
  if (prefersReducedMotion) {
    return {} as Record<string, never>;
  }
  return animationProps;
}

/**
 * Get Tailwind animation class that respects reduced motion
 *
 * Usage:
 * ```tsx
 * const prefersReducedMotion = useReducedMotion();
 * <div className={getAnimationClass(prefersReducedMotion, 'animate-bounce-in')}>
 * ```
 */
export function getAnimationClass(
  prefersReducedMotion: boolean,
  animationClass: string
): string {
  return prefersReducedMotion ? "" : animationClass;
}

export default useReducedMotion;
